import { useMemo } from 'react';
import { useAgents } from '../agents/useAgents';
import { useLiveAgentTurnSignals } from '../agents/useLiveAgentTurnSignals';
import { unseenActivityFor } from '../agents/agent-activity-utils';
import { useInboxPluginSources } from '../inbox/useInboxPluginSources';
import { useDecisionGates } from '@/hooks/useDecisionGates';
import { useAuth } from '../auth/useAuth';
import type { Agent } from '@/lib/types';

const INBOX_AGENT_STATUSES = new Set(['needs_input', 'interrupted', 'error', 'completed']);

const COMPLETED_LIMIT = 10;

/**
 * Total count of items that appear in the inbox. Recomputed from the
 * sources the view actually renders so the badge equals what the user
 * sees on the page:
 *   - decision gates (approvals — replaces the old `waiting_approval`-from-list count)
 *   - inbox flow runs: every `error`, plus `completed` capped at 10
 *   - agents: `needs_input` / `interrupted` / `error` + `running` agents
 *     gone idle, plus `completed` capped at 10
 *   - non-dismissed notifications
 *
 * `running` agents whose turn has finished are counted too — they're
 * functionally awaiting input even though their durable `status` column
 * stays `running`. "Finished" uses the same evidence-gated rule as the
 * sidebar's unseen dot and the Unread facet (`unseenActivityFor`): the
 * durable `turnState` or the live signal must say `idle` explicitly.
 *
 * Flow-run and notification counts come from the host-owned
 * {@link useInboxPluginSources} seam — this hook never imports a specific
 * plugin, so the inbox badge works against whatever plugins contribute to
 * the seam (and the seam's contract stays host-shaped regardless of which
 * plugin produces the underlying data).
 */
export function useInboxCount(): number {
  const { user } = useAuth();
  const { data: agents = [] } = useAgents({ userId: user?.id ?? null });
  const { errorFlowCount, completedFlowCount, notificationCount } = useInboxPluginSources();
  const { data: gates = [] } = useDecisionGates();
  const activities = useLiveAgentTurnSignals();

  return useMemo(() => {
    // Guard against malformed cache entries (e.g. a page with
    // `agents: undefined` leaking into the flat list). Filtering nullish
    // entries up front lets the rest of the count math stay simple.
    const active = agents.filter((a: Agent | undefined | null): a is Agent => !!a && !a.archived);
    const matchingAgents = active.filter(
      (a) =>
        INBOX_AGENT_STATUSES.has(a.status) ||
        (a.status === 'running' && unseenActivityFor(a, activities) === 'idle'),
    );
    const completedAgents = matchingAgents.filter((a) => a.status === 'completed').length;
    const nonCompletedAgents = matchingAgents.length - completedAgents;
    const agentCount = nonCompletedAgents + Math.min(completedAgents, COMPLETED_LIMIT);

    // The seam reports uncapped counts; the inbox-specific cap (10) is an
    // aggregator policy that lives here, not in the plugin.
    const completedFlows = Math.min(completedFlowCount, COMPLETED_LIMIT);
    const flowCount = errorFlowCount + completedFlows;

    return agentCount + flowCount + gates.length + notificationCount;
  }, [agents, errorFlowCount, completedFlowCount, notificationCount, activities, gates]);
}
